import { useState } from 'react';
import PropTypes from 'prop-types';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import * as Sentry from '@sentry/nextjs';

import { Button } from 'Components/LegacyButton';
import { TextField } from 'Components/TextField';
import { Typography } from 'Components/Typography';

import * as EmailListService from 'Services/EmailListService';

import { isValidEmail } from 'utils/validators';

const StyledButton = styled(Button)({
  maxWidth: 400,
});

const StyledForm = styled('form')({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
});

const StyledCard = styled('div')(
  {
    backgroundColor: theme.colors.neutral[300],
    padding: `30px ${legacyTheme.spacing.s24}`,
  },
  { name: 'StyledCard' }
);

const WaitingListForm = ({ description, fixedSubmit, initialEmail, listId, label, slug }) => {
  const [email, setEmail] = useState(initialEmail);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  return (
    <StyledForm>
      <Typography align="center" paragraph variant="p2">
        {description ?? `Sign up to be notified if this ${label} comes back in stock.`}
      </Typography>
      {!success && (
        <TextField
          error={errorMessage}
          fullWidth
          id="list_email"
          label="Enter your email address"
          onChange={event => setEmail(event.target.value)}
          value={email}
        />
      )}
      {success ? (
        <StyledCard>
          <Typography align="center" color="noir" variant="p2">
            You’ve been added to the waitlist. We’ll email you if this product becomes available
            again.
          </Typography>
        </StyledCard>
      ) : (
        <StyledButton
          data-click="join-waiting-list"
          data-testid="join-waiting-list"
          disabled={!isValidEmail(email) || isLoading}
          fixed={fixedSubmit}
          fullWidth
          isLoading={isLoading}
          onClick={async e => {
            e.preventDefault();
            setErrorMessage('');
            setIsLoading(true);
            try {
              await EmailListService.addEmailToList(email, listId);
              setSuccess(true);
            } catch {
              Sentry.captureMessage(`[WaitingListForm] subscribe to list error for ${slug}`);
              setErrorMessage('There was an error adding you to the waitlist. Please try again.');
            } finally {
              setIsLoading(false);
            }
          }}
          type="submit"
          variant="vert"
        >
          Join Waitlist
        </StyledButton>
      )}
    </StyledForm>
  );
};

WaitingListForm.defaultProps = {
  initialEmail: '',
  label: null,
  description: null,
  fixedSubmit: false,
};

WaitingListForm.propTypes = {
  /** Used in place of label and default text. */
  description: PropTypes.string,
  /** Indicate if the submit button should be fixed or not */
  fixedSubmit: PropTypes.bool,
  /** Initial value of the text input. */
  initialEmail: PropTypes.string,
  /** Id of the (Iterable) list where the email will added. */
  listId: PropTypes.number.isRequired,
  /** Label of the product subject to the waiting list */
  label: PropTypes.string,
  /** Slug of the product subject to the waiting list. For debug purposes. */
  slug: PropTypes.string.isRequired,
};

export default WaitingListForm;
